import {
  ALERT_TIERS,
  CLINICAL_RULE_APPROVALS,
  DOSE_RULE_ENFORCEMENTS,
  DOSE_RULE_KINDS,
  DOSE_RULE_SUBJECT_TYPES,
  DURATION_UNITS,
  FREQUENCY_KINDS,
  PRESCRIBING_RULE_IDS,
  STOCK_STATES,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, reasonSchema } from './clinical-common.ts';

// Prescribing and the formulary (docs/plans/phase1-consultation.md §3.3 C3, §4.1).
//
// Doses, strengths and weights cross the API as decimal strings ("2.5"), never JSON
// numbers, and are parsed by @wodi/domain on both sides. Search terms travel in POST
// bodies, never in URLs. The server's evaluation of the checks is the one stored: the
// client shows it and answers it, and never decides a tier.

export { PRESCRIBING_RULE_IDS };

/** A decimal as typed or stored: digits with an optional "." or "," fraction. */
export const decimalStringSchema = z
  .string()
  .trim()
  .min(1)
  .max(16)
  .regex(/^\d*(?:[.,]\d*)?$/, 'Numbers only, without the unit');

const codeSchema = z.string().trim().min(1).max(64);

export const codedDisplaySchema = z.object({ code: z.string(), display: z.string() });
export const codedSystemDisplaySchema = codedDisplaySchema.extend({ system: z.string() });

// --- Alerts -----------------------------------------------------------------------------------

export const prescribingAlertSchema = z.object({
  ruleId: z.enum(PRESCRIBING_RULE_IDS),
  tier: z.enum(ALERT_TIERS),
  lineKey: z.string().nullable(),
  ruleVersion: z.number().int(),
  doseRule: z
    .object({
      id: z.uuid(),
      version: z.number().int(),
      approval: z.enum(CLINICAL_RULE_APPROVALS),
      source: z.string(),
    })
    .nullable(),
  provisional: z.boolean(),
  evidence: z.array(z.string()),
  facts: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export type PrescribingAlertView = z.infer<typeof prescribingAlertSchema>;

// --- Formulary search -------------------------------------------------------------------------

export const formularySearchRequestSchema = z.object({
  query: z.string().trim().min(2).max(100),
  visitId: z.uuid(),
});

export const ingredientViewSchema = z.object({
  code: z.string(),
  display: z.string(),
  system: z.string(),
  strengthNumerator: z.object({ value: z.string(), unit: z.string() }),
  strengthDenominator: z.object({ value: z.string(), unit: z.string() }),
});

export const formularyOptionSchema = z.object({
  formularyItemId: z.uuid(),
  version: z.number().int(),
  genericName: z.string(),
  brandName: z.string().nullable(),
  strengthDisplay: z.string(),
  doseForm: codedDisplaySchema,
  doseUnit: codedDisplaySchema,
  /** Units the dose may be written in: the dose unit, and a single ingredient's measure. */
  doseUnitOptions: z.array(codedDisplaySchema),
  routes: z.array(codedSystemDisplaySchema),
  defaultRouteCode: z.string().nullable(),
  ingredients: z.array(ingredientViewSchema),
  quantityCalculable: z.boolean(),
  baseUnitContent: z.object({ value: z.string(), unit: z.string() }).nullable(),
  /** The stock base unit ("capsule", "bottle"), when stocked here. */
  baseUnit: codedDisplaySchema.nullable(),
  stock: z.object({
    state: z.enum(STOCK_STATES),
    /** Usable base units in the facility's pharmacy stores; null when not stocked. */
    usable: z.number().int().nullable(),
  }),
  isControlled: z.boolean(),
});
export type FormularyOptionView = z.infer<typeof formularyOptionSchema>;

export const formularySearchResultSchema = z.object({ items: z.array(formularyOptionSchema) });

export const frequencySchema = z.object({
  code: z.string(),
  display: z.string(),
  abbreviation: z.string().nullable(),
  kind: z.enum(FREQUENCY_KINDS),
  dosesPerDayScaled: z.number().int().nullable(),
  intervalHours: z.number().int().nullable(),
  /** "Check before go-live" until the pharmacist confirms it. */
  provisional: z.boolean(),
  status: z.enum(['active', 'inactive']),
  sortOrder: z.number().int(),
});
export type FrequencyView = z.infer<typeof frequencySchema>;

export const frequencyListSchema = z.object({ items: z.array(frequencySchema) });

// --- Draft lines, check and sign --------------------------------------------------------------

export const draftLineSchema = z.object({
  /** Chosen by the client, unique within the prescription; acknowledgements name it. */
  lineKey: z.string().trim().min(1).max(40),
  formularyItemId: z.uuid(),
  dose: z.object({ value: decimalStringSchema, unit: codeSchema }),
  routeCode: codeSchema,
  frequencyCode: codeSchema,
  /** Required for "as needed" (C26). */
  maxDosesPerDay: z.number().int().min(1).max(48).nullable().default(null),
  /** Null only for a "once" frequency. Months are not offered (P7). */
  duration: z
    .object({ value: z.number().int().min(1).max(366), unit: z.enum(DURATION_UNITS) })
    .nullable()
    .default(null),
  /** Whole base units to dispense; null to use the calculated course. */
  quantity: z.number().int().min(1).max(100_000).nullable().default(null),
  instructions: z.string().trim().max(500).nullable().default(null),
  substitutionAllowed: z.boolean().default(true),
  giveInClinic: z.boolean().default(false),
  indicationDiagnosisId: z.uuid().nullable().default(null),
});
export type DraftLineInput = z.input<typeof draftLineSchema>;
export type DraftLine = z.infer<typeof draftLineSchema>;

const uniqueKeys = (lines: readonly { lineKey: string }[]) =>
  new Set(lines.map((l) => l.lineKey)).size === lines.length;

export const checkPrescriptionRequestSchema = z.object({
  lines: z
    .array(draftLineSchema)
    .min(1)
    .max(20)
    .refine(uniqueKeys, { message: 'Each line needs its own key' }),
});
export type CheckPrescriptionRequest = z.input<typeof checkPrescriptionRequestSchema>;

export const lineCheckSchema = z.object({
  lineKey: z.string(),
  alerts: z.array(prescribingAlertSchema),
  quantityCalculated: z.number().int().nullable(),
  quantityNotCalculatedReason: z.string().nullable(),
});

export const prescriptionCheckSchema = z.object({
  prescriptionAlerts: z.array(prescribingAlertSchema),
  lines: z.array(lineCheckSchema),
  /** What the checks used, shown beside the alerts. */
  patient: z.object({
    ageDays: z.number().int().nullable(),
    ageEstimated: z.boolean(),
    weight: z
      .object({ kg: z.string(), observationId: z.uuid(), effectiveAt: z.iso.datetime() })
      .nullable(),
  }),
});
export type PrescriptionCheckView = z.infer<typeof prescriptionCheckSchema>;

export const prescribingAcknowledgementSchema = z.object({
  ruleId: z.enum(PRESCRIBING_RULE_IDS),
  /** Null for an alert about the whole prescription. */
  lineKey: z.string().max(40).nullable(),
  reason: reasonSchema,
});

export const signPrescriptionRequestSchema = z.object({
  clientRequestId: z.uuid(),
  lines: checkPrescriptionRequestSchema.shape.lines,
  acknowledgements: z.array(prescribingAcknowledgementSchema).max(200).default([]),
  /**
   * Interruptive alerts the prescriber answered by changing the line instead. Reported by
   * the client and stored marked as such (plan §12 risk 5).
   */
  seenAlerts: z
    .array(
      z.object({
        ruleId: z.enum(PRESCRIBING_RULE_IDS),
        lineKey: z.string().max(40).nullable(),
        outcome: z.literal('changed_action'),
      }),
    )
    .max(200)
    .default([]),
  /** The consultation it is written in, when there is one. */
  consultationId: z.uuid().nullable().default(null),
});
export type SignPrescriptionRequest = z.input<typeof signPrescriptionRequestSchema>;

// --- Prescriptions ----------------------------------------------------------------------------

export const PRESCRIPTION_STATUSES = ['active', 'cancelled', 'entered_in_error'] as const;
export const PRESCRIPTION_LINE_STATUSES = [
  'active',
  'stopped',
  'cancelled',
  'entered_in_error',
] as const;

export const prescriptionLineSchema = z.object({
  id: z.uuid(),
  lineNumber: z.number().int(),
  formularyItemId: z.uuid(),
  drugDisplay: z.string(),
  /** Null: "Buy outside"; the line never reaches the pharmacy. */
  serviceItemId: z.uuid().nullable(),
  dose: z.object({ value: z.string(), unit: z.string(), unitDisplay: z.string() }),
  route: codedSystemDisplaySchema,
  frequency: z.object({
    code: z.string(),
    display: z.string(),
    kind: z.enum(FREQUENCY_KINDS),
  }),
  maxDosesPerDay: z.number().int().nullable(),
  duration: z.object({ value: z.number().int(), unit: z.enum(DURATION_UNITS) }).nullable(),
  quantity: z.number().int(),
  quantityCalculated: z.number().int().nullable(),
  /** The stock base unit, for "15 capsules". */
  quantityUnit: z.string().nullable(),
  instructions: z.string().nullable(),
  substitutionAllowed: z.boolean(),
  giveInClinic: z.boolean(),
  status: z.enum(PRESCRIPTION_LINE_STATUSES),
  statusReason: z.string().nullable(),
  statusChangedAt: z.iso.datetime().nullable(),
  version: z.number().int(),
  /** The alerts stored at signing, with their outcomes. */
  alerts: z.array(
    prescribingAlertSchema.extend({
      outcome: z.enum(['acknowledged', 'changed_action', 'shown']),
    }),
  ),
});
export type PrescriptionLineView = z.infer<typeof prescriptionLineSchema>;

export const prescriptionSchema = z.object({
  id: z.uuid(),
  prescriptionNumber: z.string(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  prescriber: actorSchema,
  /** "Name, Cadre" captured at signing (consultation plan §2.13). */
  prescriberDisplay: z.string(),
  /** "COC licence …" captured at signing; null when no licence was recorded. */
  prescriberLicenceDisplay: z.string().nullable(),
  signedAt: z.iso.datetime(),
  status: z.enum(PRESCRIPTION_STATUSES),
  printCount: z.number().int(),
  prescriptionAlerts: z.array(
    prescribingAlertSchema.extend({
      outcome: z.enum(['acknowledged', 'changed_action', 'shown']),
    }),
  ),
  lines: z.array(prescriptionLineSchema),
});
export type PrescriptionView = z.infer<typeof prescriptionSchema>;

export const prescriptionListSchema = z.object({ items: z.array(prescriptionSchema) });

export const activeMedicineSchema = z.object({
  lineId: z.uuid(),
  prescriptionId: z.uuid(),
  visitId: z.uuid(),
  drugDisplay: z.string(),
  dose: z.object({ value: z.string(), unitDisplay: z.string() }),
  routeDisplay: z.string(),
  frequencyDisplay: z.string(),
  signedAt: z.iso.datetime(),
  prescriberDisplay: z.string(),
  /** When the course ends by its duration; null for "as needed" or "once" (this visit only). */
  courseEndsAt: z.iso.datetime().nullable(),
});
export const activeMedicineListSchema = z.object({ items: z.array(activeMedicineSchema) });
export type ActiveMedicineView = z.infer<typeof activeMedicineSchema>;

export const changeLineStatusRequestSchema = z.object({
  reason: reasonSchema,
  expectedVersion: z.number().int().min(1),
});

export const prescriptionPrintSchema = z.object({
  prescription: prescriptionSchema,
  facility: z.object({ name: z.string() }),
  patient: z.object({
    name: z.string(),
    patientNumber: z.string(),
    sexDisplay: z.string(),
    birthDate: z.string().nullable(),
    birthDateEstimated: z.boolean(),
  }),
  /** Allergies in words: a list, "No known allergies" or "Allergies not recorded". */
  allergies: z.array(z.string()),
  allergyState: z.enum(['allergies', 'no_known_allergies', 'not_recorded']),
  weightKg: z.string().nullable(),
  /** 1 for the first print; reprints say "Copy 2". */
  copy: z.number().int(),
});
export type PrescriptionPrintView = z.infer<typeof prescriptionPrintSchema>;

// --- Formulary administration -----------------------------------------------------------------

export const ingredientInputSchema = z.object({
  substanceCode: codeSchema,
  strengthNumerator: z.object({ value: decimalStringSchema, unit: codeSchema }),
  strengthDenominator: z.object({ value: decimalStringSchema, unit: codeSchema }),
});

export const formularyItemInputSchema = z.object({
  genericName: z.string().trim().min(1).max(200),
  brandName: z.string().trim().max(200).nullable().default(null),
  doseFormCode: codeSchema,
  strengthDisplay: z.string().trim().min(1).max(100),
  doseUnit: z.object({ code: codeSchema, display: z.string().trim().min(1).max(40) }),
  serviceItemId: z.uuid().nullable().default(null),
  baseUnitContent: z
    .object({ value: decimalStringSchema, unit: codeSchema })
    .nullable()
    .default(null),
  quantityCalculable: z.boolean().default(true),
  routeCodes: z.array(codeSchema).min(1).max(12),
  defaultRouteCode: codeSchema.nullable().default(null),
  isControlled: z.boolean().default(false),
  ingredients: z.array(ingredientInputSchema).min(1).max(10),
});
export type FormularyItemInput = z.input<typeof formularyItemInputSchema>;

export const updateFormularyItemRequestSchema = formularyItemInputSchema.extend({
  expectedVersion: z.number().int().min(1),
  status: z.enum(['active', 'inactive']).default('active'),
  statusReason: reasonSchema.nullable().default(null),
});

export const groupMembershipViewSchema = z.object({
  id: z.uuid(),
  groupKind: z.enum(['allergen_group', 'therapeutic_group']),
  group: codedSystemDisplaySchema,
  source: z.string(),
  approval: z.enum(CLINICAL_RULE_APPROVALS),
  version: z.number().int(),
  changedBy: z.uuid(),
  approvedBy: z.uuid().nullable(),
  approvedAt: z.iso.datetime().nullable(),
});

export const formularyItemAdminSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  genericName: z.string(),
  brandName: z.string().nullable(),
  doseForm: codedSystemDisplaySchema,
  strengthDisplay: z.string(),
  doseUnit: codedDisplaySchema,
  serviceItemId: z.uuid().nullable(),
  serviceItemName: z.string().nullable(),
  baseUnitContent: z.object({ value: z.string(), unit: z.string() }).nullable(),
  quantityCalculable: z.boolean(),
  routes: z.array(codedSystemDisplaySchema),
  defaultRouteCode: z.string().nullable(),
  isControlled: z.boolean(),
  status: z.enum(['active', 'inactive']),
  statusReason: z.string().nullable(),
  ingredients: z.array(ingredientViewSchema),
  /** Groups of the item itself and of its substances. */
  groups: z.array(
    groupMembershipViewSchema.extend({
      subjectType: z.enum(DOSE_RULE_SUBJECT_TYPES),
      subject: z.string(),
    }),
  ),
});
export type FormularyItemAdminView = z.infer<typeof formularyItemAdminSchema>;

export const formularyAdminListSchema = z.object({ items: z.array(formularyItemAdminSchema) });

export const replaceGroupsRequestSchema = z.object({
  subjectType: z.enum(DOSE_RULE_SUBJECT_TYPES),
  /** A substance code or a formulary item ID. */
  subject: codeSchema,
  groups: z
    .array(
      z.object({
        groupKind: z.enum(['allergen_group', 'therapeutic_group']),
        groupCode: codeSchema,
        source: z.string().trim().min(3).max(300),
      }),
    )
    .max(20),
});

// --- Dose rules -------------------------------------------------------------------------------

export const doseRuleSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  subjectType: z.enum(DOSE_RULE_SUBJECT_TYPES),
  subject: z.string(),
  subjectDisplay: z.string(),
  routeCode: z.string().nullable(),
  kind: z.enum(DOSE_RULE_KINDS),
  ageFromDays: z.number().int(),
  ageToDays: z.number().int().nullable(),
  /** kg as decimal strings. */
  weightFromKg: z.string().nullable(),
  weightToKg: z.string().nullable(),
  low: z.string().nullable(),
  high: z.string().nullable(),
  amountUnit: z.string(),
  enforcement: z.enum(DOSE_RULE_ENFORCEMENTS),
  source: z.string(),
  approval: z.enum(CLINICAL_RULE_APPROVALS),
  changedBy: z.uuid(),
  approvedBy: z.uuid().nullable(),
  approvedAt: z.iso.datetime().nullable(),
});
export type DoseRuleView = z.infer<typeof doseRuleSchema>;

export const doseRuleListSchema = z.object({
  items: z.array(doseRuleSchema),
  /** True while no approved dose rule exists: Settings says so (plan §6.3). */
  noneApproved: z.boolean(),
});

export const doseRuleInputSchema = z.object({
  /** Present to keep a rule (unchanged or changed); absent for a new one. */
  id: z.uuid().optional(),
  routeCode: codeSchema.nullable().default(null),
  kind: z.enum(DOSE_RULE_KINDS),
  ageFromDays: z.number().int().min(0).max(54_750),
  ageToDays: z.number().int().min(1).max(54_750).nullable().default(null),
  weightFromKg: decimalStringSchema.nullable().default(null),
  weightToKg: decimalStringSchema.nullable().default(null),
  low: decimalStringSchema.nullable().default(null),
  high: decimalStringSchema.nullable().default(null),
  amountUnit: codeSchema,
  enforcement: z.enum(DOSE_RULE_ENFORCEMENTS).default('interruptive'),
  source: z.string().trim().min(3).max(300),
});

export const replaceDoseRulesRequestSchema = z.object({
  subjectType: z.enum(DOSE_RULE_SUBJECT_TYPES),
  subject: codeSchema,
  rules: z.array(doseRuleInputSchema).max(50),
});
export type ReplaceDoseRulesRequest = z.input<typeof replaceDoseRulesRequestSchema>;

export const approveRuleRequestSchema = z.object({ expectedVersion: z.number().int().min(1) });
export const retireRuleRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: reasonSchema,
});

export const frequencyInputSchema = z.object({
  code: codeSchema,
  display: z.string().trim().min(1).max(60),
  abbreviation: z.string().trim().max(12).nullable().default(null),
  kind: z.enum(FREQUENCY_KINDS),
  dosesPerDay: decimalStringSchema.nullable().default(null),
  intervalHours: z.number().int().min(1).max(168).nullable().default(null),
  /** Confirming a definition clears "Check before go-live". */
  confirmed: z.boolean().default(false),
  status: z.enum(['active', 'inactive']).default('active'),
});
export const replaceFrequenciesRequestSchema = z.object({
  items: z.array(frequencyInputSchema).min(1).max(40),
});

// --- Formulary value sets -----------------------------------------------------------------------

/** Lists the pharmacist keeps: tenant-local until the SHR names its code systems (S2, S3). */
export const FORMULARY_VALUE_SETS = [
  'dose_form',
  'medication_route',
  'drug_substance',
  'allergen_group',
  'therapeutic_group',
] as const;
export type FormularyValueSet = (typeof FORMULARY_VALUE_SETS)[number];

export const formularyConceptSchema = z.object({
  code: z.string(),
  display: z.string(),
  system: z.string(),
  status: z.enum(['active', 'inactive']),
  /** "Check before go-live": a local definition nobody has confirmed yet. */
  provisional: z.boolean(),
});
export type FormularyConceptView = z.infer<typeof formularyConceptSchema>;

export const formularyConceptInputSchema = z.object({
  display: z.string().trim().min(1).max(120),
  /** Optional; made from the display when absent. */
  code: z
    .string()
    .trim()
    .regex(/^[a-z0-9_-]{1,64}$/, 'Lower-case letters, digits, "-" and "_" only')
    .optional(),
});
